/**
 * Ambience beds: looping TomMusic beds that crossfade with where the camera is.
 *
 *  - Hub (no run): a quiet interior at night with a hearth.
 *  - Dungeon: the cave bed everywhere, plus per-room layers from the theme's room style —
 *    wet floors / pools → dripping cave water (a cistern arena also gets a running stream),
 *    warm-lit rooms → torch crackle, open-air rooms (cloister garth, no ceiling) → wind.
 *  - +field (Mossvale and other open maps): a bed per area / biome from the zone's area
 *    definitions (content `FieldDef.areas`: town, meadow, lake, forest, deep forest, rocky
 *    highlands, ruins; the mine and the grotto get the cave), plus positional water from the
 *    map's terrain (content `fieldTerrainOf(id).water`): the river, Silverstep Falls and the
 *    Glimmermere shore swell as you approach them. The barrow entrance's spots (`AmbSpot`) fade
 *    in the cave and night beds. `setWeather` / `setTimeOfDay` swap the day bed for its rain /
 *    storm / night variant.
 *
 * Every bed is a sampled loop (samples.ts, loaded lazily by need, so only the area's beds are
 * fetched); they play on the ambience bus (ducked by critical cues) and fade slowly. Missing files
 * simply stay silent. The scene also picks the mixer's shared space (outdoor / dungeon / hub).
 *
 * Also exposes the floor lookup footsteps use for surface sounds (`surfaceAt`).
 */
import { fieldTerrainOf } from '@keystone/content';
import type { DungeonDef, RoomDef, Vec2 } from '@keystone/sim';
import { type Mixer, mixer } from './mixer';
import { type SampleBank, samples } from './samples';

export type AmbKey =
  | 'amb.day'
  | 'amb.day.rain'
  | 'amb.day.storm'
  | 'amb.night'
  | 'amb.dungeon'
  | 'amb.water'
  | 'amb.interior'
  | 'amb.lake'
  | 'amb.river'
  | 'amb.stream'
  | 'amb.falls'
  | 'amb.fire'
  | 'amb.wind';
export const AMB_KEYS: readonly AmbKey[] = ['amb.day', 'amb.day.rain', 'amb.day.storm', 'amb.night', 'amb.dungeon', 'amb.water', 'amb.interior', 'amb.lake', 'amb.river', 'amb.stream', 'amb.falls', 'amb.fire', 'amb.wind'];
export type Surface = 'stone' | 'dirt' | 'wood' | 'carpet' | 'grass' | 'gravel' | 'wet';
export type Weather = 'clear' | 'rain' | 'storm';
export type TimeOfDay = 'day' | 'night';

/** What ambience needs to know about a room (a subset of the toon theme's RoomStyle). */
export interface RoomSound {
  archetype?: string;
  floor?: string;
  light?: string;
  ceiling?: string;
  arcade?: unknown;
}

export type AmbMix = Partial<Record<AmbKey, number>>;

/** Hub bed. */
export const HUB_MIX: AmbMix = { 'amb.interior': 0.8, 'amb.fire': 0.45 };

/** +field: outdoor beds by field theme (fields without areas). */
export const FIELD_MIX: Readonly<Record<string, AmbMix>> = {
  town: { 'amb.day': 0.8, 'amb.fire': 0.3 },
  meadow: { 'amb.day': 1 },
  forest: { 'amb.day': 0.8, 'amb.night': 0.2 },
};

/** +field: beds per area biome (content FieldAreaDef.biome). */
export const BIOME_MIX: Readonly<Record<string, AmbMix>> = {
  town: { 'amb.day': 0.85, 'amb.fire': 0.2 },
  meadow: { 'amb.day': 1 },
  lake: { 'amb.day': 0.8 },
  forest: { 'amb.day': 0.9, 'amb.night': 0.12 },
  deepforest: { 'amb.night': 0.8, 'amb.day': 0.3 },
  rocky: { 'amb.day': 0.55, 'amb.wind': 0.45 },
  ruins: { 'amb.day': 0.6, 'amb.wind': 0.3, 'amb.night': 0.15 },
};

/** +field: beds for specific sub-areas (by area id), over their biome. */
export const AREA_MIX: Readonly<Record<string, AmbMix>> = {
  tallowdeep: { 'amb.day': 0.3, 'amb.dungeon': 0.55, 'amb.wind': 0.25, 'amb.fire': 0.15 },
  whisper_grotto: { 'amb.dungeon': 0.8, 'amb.water': 0.5 },
  briar_camp: { 'amb.night': 0.55, 'amb.day': 0.3, 'amb.fire': 0.35 },
  brookside_farm: { 'amb.day': 1, 'amb.stream': 0.35 },
  hollow_barrow: { 'amb.day': 0.45, 'amb.night': 0.4 },
  starfall_lookout: { 'amb.day': 0.5, 'amb.wind': 0.55 },
};

/** Sub-areas that sound (and reverberate) like a cave. */
const CAVE_AREAS = new Set(['tallowdeep', 'whisper_grotto']);

/** +field: a positional bed spot (the barrow entrance): full at the centre, silent past `radius`. */
export interface AmbSpot {
  pos: Vec2;
  radius: number;
}

/** +field: gain (0..1) of a spot bed for a listener at `p`. Pure. */
export function spotGain(spot: AmbSpot, p: Vec2): number {
  const d = Math.hypot(spot.pos.x - p.x, spot.pos.y - p.y);
  if (d >= spot.radius) return 0;
  const k = 1 - d / spot.radius;
  return k * k;
}

/** Distance falloff: 1 within `near`, 0 beyond `far`, eased between. Pure. */
export function falloff(d: number, near: number, far: number): number {
  if (d <= near) return 1;
  if (d >= far) return 0;
  const k = 1 - (d - near) / (far - near);
  return k * Math.sqrt(k);
}

function distToSeg(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy || 1e-9;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function distToLine(p: Vec2, pts: readonly Vec2[]): number {
  let d = Infinity;
  for (let i = 1; i < pts.length; i++) d = Math.min(d, distToSeg(p, pts[i - 1]!, pts[i]!));
  return d;
}

/** Water features of an open map (content terrain), for positional beds and wet footsteps. */
export interface FieldWater {
  river?: readonly Vec2[];
  riverHalf?: number;
  falls?: Vec2;
  lake?: { c: Vec2; rx: number; ry: number; isle?: Vec2; island?: number };
}

/** Positional water beds at `p`. Pure. */
export function waterMix(w: FieldWater | null, p: Vec2): AmbMix {
  const m: AmbMix = {};
  if (!w) return m;
  if (w.river && w.river.length > 1) m['amb.river'] = 0.9 * falloff(distToLine(p, w.river), 5, 40);
  if (w.falls) m['amb.falls'] = falloff(Math.hypot(p.x - w.falls.x, p.y - w.falls.y), 8, 60);
  if (w.lake) {
    const L = w.lake;
    const e = Math.hypot((p.x - L.c.x) / L.rx, (p.y - L.c.y) / L.ry);
    const shore = (e - 1) * Math.min(L.rx, L.ry);
    m['amb.lake'] = 0.75 * falloff(Math.max(0, shore), 3, 30);
  }
  return m;
}

/** The area (sub-areas first: definition order) containing `p`. */
interface AreaLike {
  id: string;
  biome?: string;
  region?: string;
  shape: { points: readonly Vec2[] };
}

/** Field bed for an area (sub-area override, else its biome, else the field theme). Pure. */
export function areaMix(area: AreaLike | null, theme: string): AmbMix {
  if (area) {
    const a = AREA_MIX[area.id];
    if (a) return a;
    const b = area.biome ? BIOME_MIX[area.biome] : undefined;
    if (b) return b;
  }
  return FIELD_MIX[theme] ?? FIELD_MIX['meadow']!;
}

/** Day bed → its weather / night variant (field scenes only). Pure. */
export function withSky(m: AmbMix, weather: Weather, time: TimeOfDay): AmbMix {
  const day = m['amb.day'] ?? 0;
  if (day <= 0 || (weather === 'clear' && time === 'day')) return m;
  const out: AmbMix = { ...m };
  delete out['amb.day'];
  const key: AmbKey = weather === 'storm' ? 'amb.day.storm' : weather === 'rain' ? 'amb.day.rain' : 'amb.night';
  out[key] = Math.max(out[key] ?? 0, day);
  return out;
}

/** Per-room layer gains (0..1) from its style. Pure. */
export function roomMix(style: RoomSound | null, corridor = false): AmbMix {
  const m: AmbMix = { 'amb.dungeon': 1 };
  if (!style) {
    m['amb.wind'] = corridor ? 0.25 : 0.1;
    return m;
  }
  const a = style.archetype ?? '';
  const floor = style.floor ?? '';
  if (/wet|water|pool/.test(floor) || /pool|cistern|sanctum/.test(a)) m['amb.water'] = 0.85;
  if (/sanctum|cistern/.test(a)) m['amb.stream'] = 0.45;
  if (style.light === 'warm') m['amb.fire'] = 0.4;
  if (style.ceiling === 'none' || style.arcade || /cloister|outdoor|court|garden/.test(a)) m['amb.wind'] = 0.5;
  else if (style.ceiling === 'cave') m['amb.wind'] = 0.12;
  return m;
}

/** Footstep surface for a floor pattern name. Pure. */
export function surfaceOf(floor: string | undefined): Surface {
  const f = (floor ?? '').toLowerCase();
  if (/wet|water|puddle|marsh|swamp/.test(f)) return 'wet';
  if (/grass|moss|leaf/.test(f)) return 'grass';
  if (/wood|plank|board|deck/.test(f)) return 'wood';
  if (/carpet|rug|runner/.test(f)) return 'carpet';
  if (/gravel|scree|rubble/.test(f)) return 'gravel';
  if (/dirt|mud|sand|earth/.test(f)) return 'dirt';
  return 'stone';
}

/** Outdoor footstep surface by area biome (off paths and plazas). */
export const BIOME_SURFACE: Readonly<Record<string, Surface>> = {
  town: 'grass',
  meadow: 'grass',
  lake: 'grass',
  forest: 'dirt',
  deepforest: 'dirt',
  rocky: 'gravel',
  ruins: 'grass',
};

/** Ground weights the renderer knows (0..1 at a sim point): town cobbles and roads / trails. */
export interface GroundSampler {
  plaza?: (x: number, y: number) => number;
  path?: (x: number, y: number) => number;
}

function inPoly(p: Vec2, pts: readonly Vec2[]): boolean {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y || 1e-9) + a.x) c = !c;
  }
  return c;
}

/**
 * +field: what an open map sounds like underfoot and around you: its areas (first containing
 * area wins, sub-areas first), water (river strip / lake shallows → wet), docks (wood) and the
 * renderer's cobbles / roads. Built once per map.
 */
export class FieldSoundMap {
  readonly areas: readonly AreaLike[];
  readonly water: FieldWater | null;
  private readonly docks: { a: Vec2; b: Vec2 }[] = [];
  private last: AreaLike | null = null;

  constructor(
    d: Pick<DungeonDef, 'id' | 'props' | 'field'>,
    private readonly ground: GroundSampler = {},
  ) {
    this.areas = (d.field?.areas ?? []) as readonly AreaLike[];
    let w: FieldWater | null = null;
    try {
      w = (fieldTerrainOf(d.id)?.water as FieldWater | null | undefined) ?? null;
    } catch {
      w = null;
    }
    this.water = w;
    // Docks: a plank walk ~8 yd long running out into the water (toward the lake when there is one).
    for (const p of d.props ?? []) {
      if ((p as { kind?: string }).kind !== 'dock') continue;
      const at = (p as { pos: Vec2 }).pos;
      const to = w?.lake?.c;
      let dx = to ? to.x - at.x : Math.cos((p as { facing?: number }).facing ?? 0);
      let dy = to ? to.y - at.y : Math.sin((p as { facing?: number }).facing ?? 0);
      const l = Math.hypot(dx, dy) || 1;
      dx /= l;
      dy /= l;
      this.docks.push({ a: at, b: { x: at.x + dx * 8.5, y: at.y + dy * 8.5 } });
    }
  }

  /** The area containing `p` (cached: you rarely change areas). */
  areaAt(p: Vec2): AreaLike | null {
    // Sub-areas are listed first: re-check from the top unless still inside a sub-area.
    if (this.last && !this.isRegion(this.last) && inPoly(p, this.last.shape.points)) return this.last;
    for (const a of this.areas) {
      if (inPoly(p, a.shape.points)) {
        this.last = a;
        return a;
      }
    }
    this.last = null;
    return null;
  }

  private isRegion(a: AreaLike): boolean {
    return !a.region;
  }

  surfaceAt(p: Vec2): Surface {
    for (const k of this.docks) if (distToSeg(p, k.a, k.b) < 1.3) return 'wood';
    const w = this.water;
    if (w?.river && distToLine(p, w.river) < (w.riverHalf ?? 2.6)) return 'wet';
    if (w?.lake) {
      const L = w.lake;
      const onIsle = L.isle && L.island ? Math.hypot(p.x - L.isle.x, p.y - L.isle.y) < L.island : false;
      if (!onIsle && Math.hypot((p.x - L.c.x) / L.rx, (p.y - L.c.y) / L.ry) < 1) return 'wet';
    }
    if ((this.ground.plaza?.(p.x, p.y) ?? 0) > 0.5) return 'stone';
    const area = this.areaAt(p);
    if (area && CAVE_AREAS.has(area.id)) return 'gravel';
    const biome = area?.biome ?? '';
    if ((this.ground.path?.(p.x, p.y) ?? 0) > 0.45) return biome === 'rocky' || biome === 'ruins' || biome === 'town' ? 'stone' : 'dirt';
    return BIOME_SURFACE[biome] ?? 'grass';
  }
}

/** Which room a sim-plane point is in (last hit cached: units rarely change rooms). */
export class RoomLocator {
  private last: RoomDef | null = null;
  constructor(private readonly rooms: readonly RoomDef[]) {}

  at(p: Vec2): RoomDef | null {
    if (this.last && inPoly(p, this.last.shape.points)) return this.last;
    for (const r of this.rooms) {
      if (r !== this.last && inPoly(p, r.shape.points)) {
        this.last = r;
        return r;
      }
    }
    return null;
  }
}

interface Layer {
  src: AudioBufferSourceNode;
  gain: GainNode;
}

export class Ambience {
  private scene: 'none' | 'hub' | 'dungeon' | 'field' = 'none';
  // +field: outdoor state (surface / area lookup, water, the barrow spots).
  private fieldKind = '';
  private fieldMap: FieldSoundMap | null = null;
  private fieldSurface: ((p: Vec2) => Surface) | null = null;
  private spots: AmbSpot[] = [];
  private spotLevel = 0;
  private lastFocus: Vec2 = { x: NaN, y: NaN };
  private weather: Weather = 'clear';
  private time: TimeOfDay = 'day';
  private locator: RoomLocator | null = null;
  private style: ((r: RoomDef) => RoomSound) | null = null;
  private target: AmbMix = {};
  private readonly layers = new Map<AmbKey, Layer>();
  private lastRoom: RoomDef | null | undefined = undefined;
  private readonly surfaceCache = new Map<string, Surface>();
  private corridorSurface: Surface = 'stone';
  private seq = 0;

  constructor(
    private readonly mix: Mixer = mixer,
    private readonly bank: SampleBank = samples,
  ) {}

  /**
   * +field: an open field or the town. `surface` overrides footstep surfaces; `spots` are the
   * positional barrow beds; `d` (the map) gives areas, water and docks for area beds, positional
   * water and surfaces (`ground`: the renderer's cobble / road weights).
   */
  setField(kind: string, surface: ((p: Vec2) => Surface) | null, spots: readonly AmbSpot[] = [], d?: Pick<DungeonDef, 'id' | 'props' | 'field'>, ground?: GroundSampler): void {
    this.scene = 'field';
    this.fieldKind = kind;
    this.locator = null;
    this.style = null;
    this.fieldMap = d ? new FieldSoundMap(d, ground) : null;
    this.fieldSurface = surface ?? (this.fieldMap ? (p) => this.fieldMap!.surfaceAt(p) : null);
    this.spots = [...spots];
    this.spotLevel = 0;
    this.surfaceCache.clear();
    this.lastRoom = undefined;
    this.lastFocus = { x: NaN, y: NaN };
    this.mix.setSpace('outdoor');
    this.apply(withSky(FIELD_MIX[kind] ?? FIELD_MIX['meadow']!, this.weather, this.time));
  }

  /** Hub / menus. */
  setHub(): void {
    this.leaveField();
    if (this.scene === 'hub') return;
    this.scene = 'hub';
    this.locator = null;
    this.style = null;
    this.surfaceCache.clear();
    this.mix.setSpace('hub');
    this.apply(HUB_MIX);
  }

  /** A dungeon was built: `style` gives each room's look (toon theme `room()`). */
  setDungeon(d: DungeonDef, style: (r: RoomDef) => RoomSound, corridorFloor?: string): void {
    this.leaveField();
    this.scene = 'dungeon';
    this.locator = new RoomLocator(d.rooms);
    this.style = style;
    this.surfaceCache.clear();
    this.corridorSurface = surfaceOf(corridorFloor);
    this.lastRoom = undefined;
    this.mix.setSpace('dungeon');
    this.apply({ 'amb.dungeon': 1 });
  }

  /** Silence every bed (e.g. while the tab is hidden the mixer suspends anyway). */
  stop(): void {
    this.leaveField();
    this.scene = 'none';
    this.mix.setSpace('none');
    this.apply({});
  }

  /** Weather over open maps: the day bed becomes its rain / storm variant. */
  setWeather(w: Weather): void {
    if (w === this.weather) return;
    this.weather = w;
    this.refreshField();
  }

  /** Time of day over open maps: night swaps the day bed for the night forest. */
  setTimeOfDay(t: TimeOfDay): void {
    if (t === this.time) return;
    this.time = t;
    this.refreshField();
  }

  private refreshField(): void {
    if (this.scene !== 'field') return;
    this.lastFocus = { x: NaN, y: NaN };
    if (!this.fieldMap) this.apply(withSky(FIELD_MIX[this.fieldKind] ?? FIELD_MIX['meadow']!, this.weather, this.time));
  }

  /** Per frame: follow the camera focus (sim plane) between rooms / areas. Cheap when nothing changed. */
  update(focus: Vec2): void {
    if (this.scene === 'field') {
      this.updateField(focus);
      return;
    }
    if (this.scene !== 'dungeon' || !this.locator || !this.style) return;
    const r = this.locator.at(focus);
    if (r === this.lastRoom) return;
    this.lastRoom = r;
    let s: RoomSound | null = null;
    try {
      s = r ? this.style(r) : null;
    } catch {
      s = null;
    }
    this.apply(roomMix(s, !r));
  }

  /** Footstep surface at a sim-plane point. */
  surfaceAt(p: Vec2): Surface {
    if (this.scene === 'field') return this.fieldSurface?.(p) ?? 'grass';
    if (!this.locator || !this.style) return 'stone';
    const r = this.locator.at(p);
    if (!r) return this.corridorSurface;
    let s = this.surfaceCache.get(r.id);
    if (!s) {
      try {
        s = surfaceOf(this.style(r).floor);
      } catch {
        s = 'stone';
      }
      this.surfaceCache.set(r.id, s);
    }
    return s;
  }

  /** A dungeon (or field) scene is active (a run's renderer is up). */
  get inRun(): boolean {
    return this.scene === 'dungeon' || this.scene === 'field';
  }

  /** +field: the current barrow-spot level (tests / debugging). */
  get humGain(): number {
    return this.spotLevel;
  }

  // ---- +field ------------------------------------------------------------------------------

  /** The field bed for a listener at `focus` (area bed + positional water + barrow spots + sky). Pure apart from the area cache. */
  fieldMixAt(focus: Vec2): AmbMix {
    const area = this.fieldMap?.areaAt(focus) ?? null;
    const m: AmbMix = { ...(this.fieldMap ? areaMix(area, this.fieldKind) : (FIELD_MIX[this.fieldKind] ?? FIELD_MIX['meadow']!)) };
    for (const [k, g] of Object.entries(waterMix(this.fieldMap?.water ?? null, focus)) as [AmbKey, number][]) if (g > 0.01) m[k] = Math.max(m[k] ?? 0, g);
    let sg = 0;
    for (const s of this.spots) sg = Math.max(sg, spotGain(s, focus));
    this.spotLevel = sg;
    if (sg > 0.01) {
      m['amb.dungeon'] = Math.max(m['amb.dungeon'] ?? 0, 0.7 * sg);
      m['amb.night'] = Math.max(m['amb.night'] ?? 0, 0.35 * sg);
    }
    return withSky(m, this.weather, this.time);
  }

  private updateField(focus: Vec2): void {
    // Re-evaluate after ~1 yd of camera travel (area lookups and fades are not per-frame work).
    if (Math.hypot(focus.x - this.lastFocus.x, focus.y - this.lastFocus.y) < 1) return;
    this.lastFocus = { x: focus.x, y: focus.y };
    const m = this.fieldMixAt(focus);
    const area = this.fieldMap?.areaAt(focus);
    this.mix.setSpace(area && CAVE_AREAS.has(area.id) ? 'dungeon' : 'outdoor');
    let changed = false;
    for (const k of AMB_KEYS) if (Math.abs((m[k] ?? 0) - (this.target[k] ?? 0)) > 0.02) changed = true;
    if (changed) this.apply(m);
  }

  private leaveField(): void {
    this.fieldMap = null;
    this.fieldSurface = null;
    this.spots = [];
    this.spotLevel = 0;
  }

  /** The current target mix (tests / debugging). */
  get mixTarget(): Readonly<AmbMix> {
    return this.target;
  }

  private apply(m: AmbMix): void {
    this.target = m;
    const seq = ++this.seq;
    const go = (): void => {
      if (seq !== this.seq) return;
      for (const k of AMB_KEYS) this.fade(k, m[k] ?? 0);
    };
    this.mix.onReady(() => {
      go();
      // Load the needed loops (lazily: only this area's beds), then fade them in.
      for (const k of AMB_KEYS) if ((m[k] ?? 0) > 0 && !this.bank.loopBuffer(k)) void this.bank.loadLoop(k).then(() => go());
    });
  }

  private fade(k: AmbKey, to: number): void {
    const ctx = this.mix.ctx;
    if (!ctx) return;
    let l = this.layers.get(k);
    if (!l && to <= 0) return;
    try {
      if (!l) {
        const buf = this.bank.loopBuffer(k);
        const bus = this.mix.input('amb');
        if (!buf || !bus) return;
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        const dur = this.bank.loopDuration(k);
        const start = this.bank.loopStart(k);
        const span = dur > 0 && start + dur <= buf.duration + 1e-3 ? dur : buf.duration - start;
        src.loopStart = start;
        src.loopEnd = start + span;
        const gain = ctx.createGain();
        gain.gain.value = 0;
        src.connect(gain).connect(bus);
        // Random start so two visits don't sound identical.
        src.start(ctx.currentTime + 0.05, start + Math.random() * Math.max(0, span - 0.1));
        l = { src, gain };
        this.layers.set(k, l);
      }
      const now = ctx.currentTime;
      l.gain.gain.cancelScheduledValues(now);
      l.gain.gain.setTargetAtTime(to, now, to > 0 ? 1.2 : 0.8);
      if (to <= 0) {
        const dead = l;
        this.layers.delete(k);
        dead.src.stop(now + 4);
        dead.src.onended = (): void => {
          try {
            dead.gain.disconnect();
          } catch {
            /* gone */
          }
        };
      }
    } catch {
      /* never break the game over ambience */
    }
  }
}

/** The shared ambience manager. */
export const ambience = new Ambience();
